import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface SeatAssignment {
  assignmentId: string;
  chartId: string;
  chartName: string;
  chartYear: number;
  sectionName: string;
  row: string;
  number: number;
  notes: string | null;
}

export function MySeatsPage() {
  useAuth();

  const { data: seats, isLoading } = useQuery({
    queryKey: ['my-seats'],
    queryFn: async () => {
      const response = await api.get<SeatAssignment[]>('/seating/my-seats');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Seats</h1>
          <p className="mt-2 text-gray-600">
            Your High Holiday seat assignments
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : !seats || seats.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No seat assignments yet</h3>
            <p className="text-gray-500">
              Contact the office for seat assignments.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {seats.map((seat) => (
              <div
                key={seat.assignmentId}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{seat.chartName}</h3>
                    <p className="mt-1 text-primary-600 font-medium">{seat.sectionName}</p>
                    <p className="mt-2 text-gray-600">
                      Row {seat.row}, Seat {seat.number}
                    </p>
                    {seat.notes && (
                      <p className="mt-2 text-sm text-gray-500">{seat.notes}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    <span className="inline-flex items-center rounded-lg bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700">
                      {seat.chartYear}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
